import React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { useLocale } from 'locale';
import { RootAction, State } from 'store/state';
import { PromotionPlanInfo } from 'store/sagas/apiService/types';
import {
  isPrivateBulletinMinimumPromotionPlanSelector,
  privateBulletinMinimumPromotionPlanModalBulletinIdSelector,
  promotionMiniPlanSelector,
  isTranzilaHostedFieldsModeSelector,
} from 'store/state/app/private-user-monetization/selectors';
import { setHostedFieldsModalAction, setPrivateBulletinMinimumPromotionPlanModal } from 'store/state/app/actions';
import { privateUserIdSelector } from 'store/state/domainData/selectors';
import { PAYMENT_PROCESS_ID } from 'consts/localStorageKeys';
import Modal from 'ds/components/modal';
import { planEnhancer } from 'components/private-bulletin-promotion/helpers';
import { handlePromotionPlanSelect } from '../shared/handlePromotionPlanSelect';
import { INITIATE_SOURCE } from 'components/private-bulletin-promotion/types';
import LogoSmall from 'assets/svg/il-logo-small.svg';
import { Wrapper, HeaderBlock, Headline, Subtitle, TermsLink } from './styled';
import { OfferTimer } from './OfferTimer';
import { OfferCard } from './OfferCard';
import { TranzilaModalType } from 'components/tranzila-hosted-fields/types';
import withEvents from 'analytics/withEvents';
import { PrivateUserMonetizationSource } from 'store/state/app';
import { privateUserMonetizationEventsSourceSelector } from 'store/state/app/selectors';
import { TermsPageSectionId, TermsPageSource } from 'screens/TermsPage/types';
import { useTermsPageHref } from 'screens/TermsPage/useTermsPageHref';
import { makeVariantToEventsString } from 'store/sagas/analytics/utils';

interface PrivateBulletinMinimumPromotionPlanModalProps {
  isOpen: boolean;
  plan: PromotionPlanInfo;
  userId: string;
  onClose: () => void;
  onPlanSelect?: (plan: PromotionPlanInfo, key: string, bulletinId: string | null) => void;
  bulletinId: string | null;
  isHostedFieldsMode?: boolean;
  onHostedFieldsPlanSelect: (plan: PromotionPlanInfo, bulletinId: string | null) => void;
  eventSource?: PrivateUserMonetizationSource;
}

const PrivateBulletinMinimumPromotionPlanModalBase: React.FC<PrivateBulletinMinimumPromotionPlanModalProps> = props => {
  const { isOpen, plan: rawPlan, userId, onClose, onPlanSelect, bulletinId, isHostedFieldsMode, onHostedFieldsPlanSelect } = props;
  const { t } = useLocale();
  const termsOfServiceHref = useTermsPageHref({
    source: TermsPageSource.MiniPromotionPlanOfferModal,
    sectionId: TermsPageSectionId.PrivateListingPromotionPlanTerms,
  });

  if (!isOpen || !rawPlan) return null;

  const enhancedPlans = planEnhancer([ rawPlan ], t);
  const plan = enhancedPlans[0];

  if (!plan) return null;

  const timerEndDate = plan.offerValidUntil ? new Date(plan.offerValidUntil) : null;

  const handlePlanSelect = () => {
    if (isHostedFieldsMode && !plan.isUserEligibleForGrant) {
      onHostedFieldsPlanSelect(plan, bulletinId);
    }
    else {
      onPlanSelect(plan, userId, bulletinId);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      mobileModalPositionBottom
    >
      <Wrapper>
        <HeaderBlock>
          <LogoSmall width={48} height={44} aria-hidden style={{ marginBottom: 24 }} />
          <Headline weight="bold">{t('privateBulletin.promotion.minimum.modal.headline', { isTimeLimited: Boolean(timerEndDate) })}</Headline>
          <Subtitle>{t('privateBulletin.promotion.minimum.modal.subtitle')}</Subtitle>
        </HeaderBlock>

        {timerEndDate ? <OfferTimer endDate={timerEndDate.getTime()} /> : null}

        <OfferCard plan={plan} onPlanSelect={handlePlanSelect} />

        <TermsLink href={termsOfServiceHref} target="_blank" rel="nofollow">
          {t('privateBulletin.promotion.termsOfUse')}
        </TermsLink>
      </Wrapper>
    </Modal>
  );
};

const mapStateToProps = (state: State) => ({
  isOpen: isPrivateBulletinMinimumPromotionPlanSelector(state),
  plan: promotionMiniPlanSelector(state),
  userId: privateUserIdSelector(state),
  bulletinId: privateBulletinMinimumPromotionPlanModalBulletinIdSelector(state),
  isHostedFieldsMode: isTranzilaHostedFieldsModeSelector(state),
  eventSource: privateUserMonetizationEventsSourceSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => ({
  onClose: () => dispatch(setPrivateBulletinMinimumPromotionPlanModal({ isOpen: false, bulletin: null })),
  onPlanSelect: (plan: PromotionPlanInfo, userId: string, bulletinId: string | null) => {
    const key = bulletinId ? `${PAYMENT_PROCESS_ID}_${userId}_${bulletinId}` : userId;
    const closeModal = () => dispatch(setPrivateBulletinMinimumPromotionPlanModal({ isOpen: false, bulletin: null }));
    handlePromotionPlanSelect(dispatch, plan, key, INITIATE_SOURCE.TRIAL, closeModal, bulletinId);
  },
  onHostedFieldsPlanSelect: (plan: PromotionPlanInfo, bulletinId: string | null) => dispatch(setHostedFieldsModalAction({
    planInfo: plan,
    modalType: TranzilaModalType.Loading,
    paymentDealType: 'promotion_plan_trial',
    ...(bulletinId ? { bulletinId } : {}),
  })),
});

const PrivateBulletinMinimumPromotionPlanModalBaseWithEvents = withEvents<PrivateBulletinMinimumPromotionPlanModalProps>((sendEvent, props) => ({
  onHostedFieldsPlanSelect() {
    sendEvent('user_promotion_plan_select', 'user', {
      event: {
        deal_type: 'promotion_plan_trial',
        source: props.eventSource,
        promotion_plan: props.plan ? makeVariantToEventsString(props.plan.variant) : undefined,
      },
    });
  },
}))(PrivateBulletinMinimumPromotionPlanModalBase);

export const PrivateBulletinMinimumPromotionPlanModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(PrivateBulletinMinimumPromotionPlanModalBaseWithEvents);
